import { useEffect } from 'react';
import { BUILD_INFO, currentEnv, ENV_LABEL } from '../env';

/**
 * Says, unmissably, when this is not the real board.
 *
 * A test copy that looks identical to the live one is how somebody makes a real
 * change believing it is a rehearsal — so this sits above everything, in a
 * colour used nowhere else, and names the specific risk rather than just the
 * environment. On the live site it renders nothing at all.
 */
export default function EnvBanner() {
  const env = currentEnv();

  // The browser tab is the other place these get confused, when several are open.
  useEffect(() => {
    const base = 'FlightDeck — Project Tracker';
    document.title = env === 'live' ? base : `[${ENV_LABEL[env]}] ${base}`;
  }, [env]);

  if (env === 'live') return null;

  return (
    <div className={`env-banner env-${env}`} role="status" data-build={BUILD_INFO}>
      <strong>{ENV_LABEL[env]}</strong>
      <span>
        {env === 'uat'
          ? 'Test copy — not the live board. It has its own copy of the data, so nothing you do here reaches the real one.'
          : 'Running on a development machine, against the test data.'}
      </span>
      {/* Which copy this is. Every branch gets its own, they all say UAT, and
          two cut from the same release carry the same version number — so
          without this there is nothing on screen telling them apart. */}
      <span className="env-version">
        {__BUILD_BRANCH__ && __BUILD_BRANCH__ !== 'live' && (
          <strong className="env-branch">{__BUILD_BRANCH__.replace(/^feature\//, '')}</strong>
        )}
        {/* WHICH RELEASE THIS IS A CANDIDATE FOR. The version beside it counts
            the development line and has nothing to do with the number on the
            live board — so a test copy could say v1.42.0+587 while being the
            2.6.2 candidate, and the screen could not tell you which release
            you were being asked to approve. Now it says so first, because
            that is the question somebody testing came here to answer. */}
        {__RELEASE_CANDIDATE__ && (
          <strong className="env-candidate">{__RELEASE_CANDIDATE__} candidate</strong>
        )}
        {/* ⚠️ AND WHEN IT IS NOT A CANDIDATE, IT SAYS SO IN DIFFERENT WORDS.
            This read "2.7.1+29 candidate" for hours after 2.7.1 went live —
            telling whoever was testing that they were approving a release that
            had already shipped. Only a build sitting ON the tag is awaiting a
            decision; past it there is unreleased work, and that is a different
            sentence rather than the same one with a number added. */}
        {!__RELEASE_CANDIDATE__ && __SINCE_LIVE__ && (
          <strong className="env-since">unreleased · {__SINCE_LIVE__}</strong>
        )}
        v{__APP_VERSION__} · {__BUILD_COMMIT__}
      </span>
    </div>
  );
}
